// React and Next.js imports
import type { GetStaticProps, GetStaticPaths } from "next";
import "@/registry/builder-registry";
import type { BasePageProps } from "@orlandohealth/build-kit";
import { GenericPage } from "@orlandohealth/build-kit";
import { GenericWrapper } from "@orlandohealth/build-kit";
import { logger } from "@orlandohealth/utils";
import { getAllBuilderContent } from "@orlandohealth/build-kit";

// Explicitly pass GenericWrapper as the wrapper component.  This is the default wrapper so
// it's not necessary to pass it but providing it here for clarity.
const Page = GenericPage.createPage(GenericWrapper);

export const getStaticProps: GetStaticProps<BasePageProps> = async ({
  params,
  locale,
  defaultLocale,
  preview,
  previewData = {},
}) => {
  let urlPath = "";
  try {
    // Check for test error parameter
    urlPath =
      "/" +
      (Array.isArray(params?.page)
        ? params.page.join("/")
        : params?.page || "");

    logger.info(`Processing page: ${urlPath}`, "static-props");

    // Check if we're in Builder by looking at previewData
    const isInBuilder =
      !!(previewData as any)?.["builder.space"] ||
      !!(previewData as any)?.["builder.frameEditing"];

    logger.info(`Fetching content for page: ${urlPath}`, "static-props");
    // For other pages, try to get the content
    const result = await GenericPage.getStaticProps(
      urlPath,
      locale,
      defaultLocale,
      "page",
      preview || isInBuilder
    );

    logger.info(`Content fetched for page: ${urlPath}`, "static-props");

    // If no content was found and we're not in preview mode, return notFound
    if (!result.props.content && !preview && !isInBuilder) {
      logger.info(`No content found for page: ${urlPath}`, "static-props");
      return {
        notFound: true,
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    }

    logger.info(`Successfully processed page: ${urlPath}`, "static-props");
    return result;
  } catch (error: unknown) {
    logger.error(`Error processing page ${urlPath}: ${error}`, "static-props");
    return {
      notFound: true,
      revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
    };
  }
};

// You might want to move these to a config file like config/routing.ts
const EXCLUDED_DIRECTORIES = [
  "/services",
  "/physician-finder",
  "/clinical-trials",
  "/content-hub",
  "/search",
  "/dynamiclink",
  "/section-editors",
  "/medical-professionals",
  "/facilities",
];

// Helper function to check if a path should be excluded
const shouldExcludePath = (url: string): boolean => {
  // Check if the path starts with any of the excluded directories (case-insensitive)
  const lowerUrl = url.toLowerCase();
  const isExcludedDirectory = EXCLUDED_DIRECTORIES.some((dir) =>
    lowerUrl.startsWith(dir.toLowerCase())
  );

  // Also exclude standalone pages that have their own implementations
  const standalonePages = ["/404", "/error", "/500"];
  const isStandalonePage = standalonePages.includes(url);

  return isExcludedDirectory || isStandalonePage || url === "/";
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (process.env.SKIP_SSG === "true") { return { paths: [], fallback: "blocking" }; }
  try {
    logger.info("Fetching pages from Builder.io...", "static-paths");
    const pages = await getAllBuilderContent("page", {
      fields: "data.url",
      options: { noTargeting: true },
    });

    const paths = pages.flatMap((page) => {
      const url = String(page.data?.url);
      const isValid = url !== "/" && !shouldExcludePath(url);
      if (!isValid) {
        logger.debug(`Excluding page: ${url}`, "static-paths");
        if (url === "/") {
          logger.debug("Reason: Root path", "static-paths");
        } else if (shouldExcludePath(url)) {
          if (EXCLUDED_DIRECTORIES.some((dir) => url.startsWith(dir))) {
            logger.debug("Reason: Matches excluded directory", "static-paths");
          } else {
            logger.debug("Reason: Is a standalone page", "static-paths");
          }
        }
        return [];
      }

      // Normalize URL to lowercase to ensure consistent file naming
      // This prevents case mismatch issues during static export on case-insensitive filesystems
      const normalizedUrl = url.toLowerCase();
      return (locales || []).map((locale) => ({
        params: {
          page: normalizedUrl.split("/").filter(Boolean),
        },
        locale,
      }));
    });

    logger.debug(
      `Generated ${paths.length} paths for static generation`,
      "static-paths"
    );
    paths.forEach((path, index) => {
      logger.debug(
        `${index + 1}. /${path.params.page.join("/")}`,
        "static-paths"
      );
    });

    return {
      paths,
      fallback: "blocking",
    };
  } catch (error) {
    logger.error(`Error in getStaticPaths: ${error}`, "static-paths");
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

export default Page;
